import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardText } from "@phosphor-icons/react";

const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

export default function SuperAdminAudit() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/superadmin/impersonation/events", { params: { limit: 100 } })
            .then((r) => setEvents(r.data.items || []))
            .catch(() => toast.error("Failed to load audit log"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="superadmin-audit">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1">Compliance</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Audit log</h1>
            <p className="text-sm text-[#525252] mb-6">Impersonation sessions across all tenants.</p>

            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && events.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <ClipboardText size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#525252]">No impersonation events yet.</p>
                </div>
            )}
            {events.length > 0 && (
                <>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-3">
                    {events.map((e) => (
                        <div key={e.id} data-testid={`audit-card-${e.id}`} className="bg-white border border-[#E5E5E5] p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                    <p className="text-xs font-heading font-bold text-ink truncate">{e.actor_email}</p>
                                    <p className="text-[10px] text-[#A3A3A3]">Started {fmtTime(e.started_at)}</p>
                                </div>
                                {!e.ended_at ? (
                                    <span className="text-[10px] font-black uppercase text-emerald-600 shrink-0">Active</span>
                                ) : (
                                    <span className="text-[10px] text-[#A3A3A3] shrink-0">Ended {fmtTime(e.ended_at)}</span>
                                )}
                            </div>
                            <div className="pt-2 border-t border-[#F5F5F5] space-y-1">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-16 shrink-0">Target</span>
                                    <span className="truncate">{e.target_email}</span>
                                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252] shrink-0">{e.target_role}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-16 shrink-0">Workspace</span>
                                    <span className="font-mono text-[#525252] truncate">{e.workspace_id}</span>
                                </div>
                                {e.reason && (
                                    <div className="flex items-start gap-2 text-xs">
                                        <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-16 shrink-0 mt-0.5">Reason</span>
                                        <span className="text-[#525252]">{e.reason}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["Started", "Ended", "Actor", "Target", "Role", "Workspace", "Reason"].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((e) => (
                                <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-b border-[#F5F5F5]">
                                    <td className="px-4 py-3 text-xs">{fmtTime(e.started_at)}</td>
                                    <td className="px-4 py-3 text-xs">{e.ended_at ? fmtTime(e.ended_at) : (
                                        <span className="text-emerald-600 font-black text-[10px] uppercase">Active</span>
                                    )}</td>
                                    <td className="px-4 py-3 text-xs">{e.actor_email}</td>
                                    <td className="px-4 py-3 text-xs">{e.target_email}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">
                                            {e.target_role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-[#525252]">{e.workspace_id}</td>
                                    <td className="px-4 py-3 text-xs text-[#525252] max-w-[200px] truncate" title={e.reason || ""}>
                                        {e.reason || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </div>
    );
}
